import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TimezoneDetectionManager } from '@/components/timezone/TimezoneDetectionManager';
import { UTMNavigate } from '@/components/custom-ui/utm-navigate';
import { BrandsProvider } from '@/lib/BrandsProvider.tsx';
import { AuthProvider, useAuth } from '@/lib/auth.tsx';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Auth } from '@/components/public/auth/Auth';
import Home from '@/components/public/sections/Home';
import { routes, RouteType } from './routes/index';
import { BrandGenius } from "@/components/brand/BrandGenius";
import ContentTypeSelection from '@/components/content/ContentTypeSelection'
import { WizardLayout } from '@/components/onboarding/WizardLayout';
import Layout from '@/components/Layout';
import UnauthenticatedLayout from '@/components/public/layout/UnauthenticatedLayout';
import LogoutPage from '@/components/auth/LogoutPage';
// import { VisibilityHandler } from './lib/visibilityHandler'
import { useEffect, Suspense, lazy, useState } from 'react';
import PricingPage from '@/components/public/pricing/PricingPage';
import AuthCallback from '@/components/public/auth/AuthCallback';
import LinkedInCallback from '@/components/auth/LinkedInCallback';
import TwitterCallback from '@/components/auth/TwitterCallback';
import FacebookCallback from '@/components/auth/FacebookCallback';
import { AcceptBrandInvitation } from '@/components/invitations/AcceptBrandInvitation';
import ProcessInvitation from '@/components/public/auth/process-invitation';
import { AnalyticsProvider, PostHogAdapter, useAnalytics } from '@/lib/analytics';
import ErrorBoundary from '@/components/custom-ui/error-boundary';
import { SignupPage, LoginPage } from '@/components/auth';
import { PasswordResetForm } from '@/components/auth/PasswordResetForm';
import { ResetPasswordConfirm } from '@/components/auth/ResetPasswordConfirm';
import { AccountProvider } from '@/lib/AccountProvider';
import { BillingGuard } from '@/components/BillingGuard';
import { supabase } from '@/lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';
import PurchaseSuccess from '@/components/settings/PurchaseSuccess';
import { DevPasswordProtection } from '@/components/DevPasswordProtection';
import { LoadingTransition } from '@/components/transitions/PageTransition';

// Lazy loaded components
const AnonymousOnboarding = lazy(() => import('@/components/public/pages/AnonymousOnboarding'));
const AboutUs = lazy(() => import('@/components/public/pages/AboutUs'));
const Services = lazy(() => import('@/components/public/pages/Services'));
const Products = lazy(() => import('@/components/public/pages/Products'));
const Contact = lazy(() => import('@/components/public/pages/Contact'));
const Careers = lazy(() => import('@/components/public/pages/Careers'));
const PrivacyPolicy = lazy(() => import('@/components/public/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/components/public/pages/TermsOfService'));
const CookieSettings = lazy(() => import('@/components/public/pages/CookieSettings'));
const Lander1 = lazy(() => import('@/components/public/pages/Lander1'));
const Blog = lazy(() => import('@/components/public/pages/Blog'));
const BlogPost = lazy(() => import('@/components/public/pages/BlogPost'));
const LandingPage = lazy(() => import('@/components/public/sections/LandingPage'));
const BrandPreview = lazy(() => import('@/components/public/pages/BrandPreview'));
const PublicContentView = lazy(() => import('@/components/public/PublicContentView'));

// Initialize PostHog adapter with error handling
function App() {
    // Use a try-catch to prevent any analytics initialization errors from breaking the app
    let analyticsAdapter;
    try {
        analyticsAdapter = new PostHogAdapter();
    } catch (error) {
        console.debug('Failed to initialize analytics adapter:', error);
        // Create a dummy adapter that implements the interface but does nothing
        analyticsAdapter = {
            initialize: () => {},
            identify: () => {},
            track: () => {},
            page: () => {},
            reset: () => {},
            enable: () => {},
            disable: () => {},
        };
    }

    return (
        <DevPasswordProtection>
            <Router>
                <AuthProvider>
                    <ErrorBoundary fallback={
                        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
                            <h2 className="text-2xl font-bold mb-4">We're having trouble loading analytics</h2>
                            <p className="mb-4">The application will continue to work without analytics.</p>
                            <AppContent />
                        </div>
                    }>
                        <AnalyticsProvider 
                            adapter={analyticsAdapter}
                            options={{
                                apiKey: import.meta.env.VITE_POSTHOG_API_KEY || 'dummy-key-for-blocked-analytics',
                                apiHost: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
                            }}
                        >
                            <AppContent />
                        </AnalyticsProvider>
                    </ErrorBoundary>
                </AuthProvider>
            </Router>
        </DevPasswordProtection>
    );
}

function AppContent() {
    const { session, loading } = useAuth();
    const { identify, enabled } = useAnalytics();
    const location = useLocation();
    const navigate = useNavigate();
    const [checkingOnboarding, setCheckingOnboarding] = useState(false);
    
    // Debug re-renders
    useEffect(() => {
        console.log('[AppContent] Component rendered/re-rendered', {
            sessionId: session?.user?.id,
            loading,
            pathname: location.pathname,
            timestamp: new Date().toISOString()
        });
    });

    // Temporarily disabled visibility handler to fix navigation flashing
    // TODO: Implement a better solution for Supabase connection recovery
    // that doesn't interfere with navigation
    
    // Identify user when session is available
    useEffect(() => {
        if (session?.user && enabled) {
            identify({
                id: session.user.id,
                traits: {
                    email: session.user.email,
                    last_login: new Date().toISOString()
                }
            });
        }
    }, [session, identify, enabled]);

    // Check onboarding status when user logs in
    useEffect(() => {
        const checkOnboarding = async () => {
            if (!session?.user) return;
            
            // Skip check if we're already on the onboarding page, settings, or auth-related pages
            const skipPaths = ['/onboarding', '/settings', '/auth/', '/invitations/', '/process-invitation', '/logout', '/checkout', '/purchase-success'];
            if (skipPaths.some(path => location.pathname.includes(path))) {
                return;
            }

            setCheckingOnboarding(true);
            try {
                const { data: profile, error } = await supabase
                    .schema('marketing_and_customer_success')
                    .from('profiles')
                    .select('onboarding_completed')
                    .eq('id', session.user.id)
                    .single();

                if (!error && profile && profile.onboarding_completed === false) {
                    console.log('[App] User onboarding not completed, redirecting to /onboarding');
                    navigate('/onboarding');
                }
            } catch (error) {
                console.error('[App] Error checking onboarding status:', error);
            } finally {
                setCheckingOnboarding(false);
            }
        };

        checkOnboarding();
    }, [session, location.pathname, navigate]);

    if (loading || checkingOnboarding) {
        return (
            <div className="min-h-screen">
                <LoadingTransition message={loading ? "Loading application..." : "Checking your settings..."} />
            </div>
        );
    }

    // For authenticated users, wrap everything in BrandsProvider
    if (session) {
        return (
            <BrandsProvider>
                <AccountProvider>
                    <Routes>
                        {/* AuthCallback should be available regardless of auth state */}
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
                        <Route path="/auth/twitter/callback" element={<TwitterCallback />} />
                        <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
                        
                        {/* Invitation routes for authenticated users */}
                        <Route path="/invitations/brand/:token" element={<AcceptBrandInvitation />} />
                        <Route path="/process-invitation" element={<ProcessInvitation />} />
                        
                        {/* Special routes outside of Layout */}
                        <Route path="/onboarding" element={
                            <ProtectedRoute requireBrands={false}>
                                <WizardLayout />
                            </ProtectedRoute>
                        } />
                        
                        <Route path="/logout" element={
                            <ProtectedRoute requireBrands={false}>
                                <LogoutPage />
                            </ProtectedRoute>
                        } />
                        
                        {/* Purchase success route outside of BillingGuard to prevent redirect loops */}
                        <Route path="/checkout/success" element={
                            <ProtectedRoute requireBrands={false}>
                                <PurchaseSuccess />
                            </ProtectedRoute>
                        } />
                        <Route path="/purchase-success" element={
                            <ProtectedRoute requireBrands={false}>
                                <PurchaseSuccess />
                            </ProtectedRoute>
                        } />
                        
                        {/* All other routes wrapped in Layout and BillingGuard */}
                        <Route element={<BillingGuard><Layout /></BillingGuard>}>
                        {/* Public information pages (now accessible to logged-in users) */}
                        <Route path="/landing" element={
                            <Suspense fallback={<LoadingTransition message="Loading landing page..." />}>
                                <LandingPage />
                            </Suspense>
                        } />
                        <Route path="/preview/:slug" element={
                            <Suspense fallback={<LoadingTransition message="Loading brand preview..." />}>
                                <BrandPreview />
                            </Suspense>
                        } />
                        <Route path="/about" element={
                            <Suspense fallback={<LoadingTransition message="Loading about page..." />}>
                                <AboutUs />
                            </Suspense>
                        } />
                        <Route path="/services" element={
                            <Suspense fallback={<LoadingTransition message="Loading services..." />}>
                                <Services />
                            </Suspense>
                        } />
                        <Route path="/products" element={
                            <Suspense fallback={<LoadingTransition message="Loading products..." />}>
                                <Products />
                            </Suspense>
                        } />
                        <Route path="/contact" element={
                            <Suspense fallback={<LoadingTransition message="Loading contact page..." />}>
                                <Contact />
                            </Suspense>
                        } />
                        <Route path="/careers" element={
                            <Suspense fallback={<LoadingTransition message="Loading careers page..." />}>
                                <Careers />
                            </Suspense>
                        } />
                        <Route path="/privacy-policy" element={
                            <Suspense fallback={<LoadingTransition message="Loading privacy policy..." />}>
                                <PrivacyPolicy />
                            </Suspense>
                        } />
                        <Route path="/terms-of-service" element={
                            <Suspense fallback={<LoadingTransition message="Loading terms of service..." />}>
                                <TermsOfService />
                            </Suspense>
                        } />
                        <Route path="/cookie-settings" element={
                            <Suspense fallback={<LoadingTransition message="Loading cookie settings..." />}>
                                <CookieSettings />
                            </Suspense>
                        } />
                        <Route path="/blog" element={
                            <Suspense fallback={<LoadingTransition message="Loading blog..." />}>
                                <Blog />
                            </Suspense>
                        } />
                        <Route path="/blog/:guid" element={
                            <Suspense fallback={<LoadingTransition message="Loading blog post..." />}>
                                <BlogPost />
                            </Suspense>
                        } />

                        {/* Protected app routes */}
                        {routes.map((route: RouteType) => (
                            <Route
                                key={route.path}
                                path={route.path}
                                element={
                                    <ProtectedRoute>
                                        <route.element session={session} />
                                    </ProtectedRoute>
                                }
                            />
                        ))}
                        <Route
                            path="/brand-genius"
                            element={
                                <ProtectedRoute>
                                    <BrandGenius />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/content/create"
                            element={
                                <ProtectedRoute>
                                    <ContentTypeSelection />
                                </ProtectedRoute>
                            }
                        />

                        {/* Public content routes - accessible to authenticated users too */}
                        <Route path="/public/content/:contentId" element={
                            <Suspense fallback={<LoadingTransition message="Loading content..." />}>
                                <PublicContentView />
                            </Suspense>
                        } />

                        {/* Root route for authenticated users redirects to dashboard */}
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        {/* Catch-all route for authenticated users */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                </Routes>
                
                {/* Timezone Detection Manager for authenticated users */}
                <TimezoneDetectionManager 
                    showToast={true}
                    showCustomNotification={false} // Disable custom notification since Layout handles it
                    autoTrigger={true}
                />
                </AccountProvider>
            </BrandsProvider>
        );
    }

    // For unauthenticated users
    return (
        <Routes>
            {/* Special routes that work regardless of auth state */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Public routes with UnauthenticatedLayout */}
            <Route element={<UnauthenticatedLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/landing" element={
                    <Suspense fallback={<LoadingTransition message="Loading landing page..." />}>
                        <LandingPage />
                    </Suspense>
                } />
                <Route path="/preview/:slug" element={
                    <Suspense fallback={<LoadingTransition message="Loading brand preview..." />}>
                        <BrandPreview />
                    </Suspense>
                } />
                <Route path="/auth" element={<Auth />} />
                <Route path="/login" element={<Auth />} />
                <Route path="/login-new" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/reset-password" element={<PasswordResetForm />} />
                <Route path="/reset-password/confirm" element={<ResetPasswordConfirm />} />

                <Route path="/onboarding/help-wizard" element={
                    <Suspense fallback={<LoadingTransition message="Loading onboarding wizard..." />}>
                        <AnonymousOnboarding />
                    </Suspense>
                } />
                <Route path="/lander1" element={
                    <Suspense fallback={<LoadingTransition message="Loading page..." />}>
                        <Lander1 />
                    </Suspense>
                } />
                <Route path="/invitations/brand/:token" element={<AcceptBrandInvitation />} />
                <Route path="/process-invitation" element={<ProcessInvitation />} />
                
                {/* New public pages */}
                <Route path="/about" element={
                    <Suspense fallback={<LoadingTransition message="Loading about page..." />}>
                        <AboutUs />
                    </Suspense>
                } />
                <Route path="/services" element={
                    <Suspense fallback={<LoadingTransition message="Loading services..." />}>
                        <Services />
                    </Suspense>
                } />
                <Route path="/products" element={
                    <Suspense fallback={<LoadingTransition message="Loading products..." />}>
                        <Products />
                    </Suspense>
                } />
                <Route path="/contact" element={
                    <Suspense fallback={<LoadingTransition message="Loading contact page..." />}>
                        <Contact />
                    </Suspense>
                } />
                <Route path="/careers" element={
                    <Suspense fallback={<LoadingTransition message="Loading careers page..." />}>
                        <Careers />
                    </Suspense>
                } />
                <Route path="/privacy-policy" element={
                    <Suspense fallback={<LoadingTransition message="Loading privacy policy..." />}>
                        <PrivacyPolicy />
                    </Suspense>
                } />
                <Route path="/terms-of-service" element={
                    <Suspense fallback={<LoadingTransition message="Loading terms of service..." />}>
                        <TermsOfService />
                    </Suspense>
                } />
                <Route path="/cookie-settings" element={
                    <Suspense fallback={<LoadingTransition message="Loading cookie settings..." />}>
                        <CookieSettings />
                    </Suspense>
                } />
                <Route path="/public/content/:contentId" element={
                    <Suspense fallback={<LoadingTransition message="Loading content..." />}>
                        <PublicContentView />
                    </Suspense>
                } />
                <Route path="/blog" element={
                    <Suspense fallback={<LoadingTransition message="Loading blog..." />}>
                        <Blog />
                    </Suspense>
                } />
                <Route path="/blog/:guid" element={
                    <Suspense fallback={<LoadingTransition message="Loading blog post..." />}>
                        <BlogPost />
                    </Suspense>
                } />
                
                {/* Catch-all route for unauthenticated users */}
                <Route path="*" element={<UTMNavigate to="/" replace />} />
            </Route>
        </Routes>
    );
}

export default App;
